/*
 * "Encrypt selected text" (Evernote). Engram's format, v1:
 *   base64( salt[16] | iv[12] | AES-256-GCM ciphertext+tag )
 *   key = PBKDF2-HMAC-SHA256(passphrase UTF-8, salt, 600,000 iterations)
 * The plaintext is the selection's HTML. Nothing about the passphrase is stored; the hint is plain text.
 *
 * Evernote's own format (imported <en-crypt cipher="AES" length="128">), per its developer docs:
 *   base64( "ENC0" | salt[16] | hmacSalt[16] | iv[16] | AES-128-CBC ciphertext | HMAC-SHA256[32] )
 *   keys = PBKDF2-HMAC-SHA256(passphrase, salt / hmacSalt, 50,000 iterations, 128 bits); the HMAC covers
 *   everything before it. The plaintext is an ENML fragment. Legacy RC2 ("length=64") can't be read.
 */

const ITER = 600_000;
const enc = new TextEncoder();
const dec = new TextDecoder();

function b64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
function unb64(s: string): Uint8Array {
  const bin = atob(s.replace(/\s/g, ""));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function pbkdf2(pass: string, salt: Uint8Array, iterations: number, alg: AesKeyGenParams | HmacImportParams, usage: KeyUsage[]): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", enc.encode(pass), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "PBKDF2", hash: "SHA-256", salt: salt as BufferSource, iterations }, base, alg, false, usage);
}

export class WrongPassphrase extends Error {
  constructor() {
    super("Wrong passphrase");
  }
}

export async function encryptText(plain: string, pass: string, iterations = ITER): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await pbkdf2(pass, salt, iterations, { name: "AES-GCM", length: 256 }, ["encrypt"]);
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, enc.encode(plain)));
  const out = new Uint8Array(28 + ct.length);
  out.set(salt, 0);
  out.set(iv, 16);
  out.set(ct, 28);
  return b64(out);
}

export async function decryptText(cipher: string, pass: string, iterations = ITER): Promise<string> {
  const raw = unb64(cipher);
  if (raw.length < 28 + 16) throw new Error("Damaged encrypted text");
  const key = await pbkdf2(pass, raw.subarray(0, 16), iterations, { name: "AES-GCM", length: 256 }, ["decrypt"]);
  try {
    return dec.decode(await crypto.subtle.decrypt({ name: "AES-GCM", iv: raw.subarray(16, 28) as BufferSource }, key, raw.subarray(28) as BufferSource));
  } catch {
    throw new WrongPassphrase();
  }
}

const EN_ITER = 50_000;

export async function decryptEvernote(cipher: string, pass: string, iterations = EN_ITER): Promise<string> {
  const raw = unb64(cipher);
  if (raw.length < 4 + 48 + 16 + 32 || dec.decode(raw.subarray(0, 4)) !== "ENC0") throw new Error("This encrypted text uses an old Evernote format Engram can't read.");
  const salt = raw.subarray(4, 20);
  const hmacSalt = raw.subarray(20, 36);
  const iv = raw.subarray(36, 52);
  const body = raw.subarray(0, raw.length - 32);
  const mac = raw.subarray(raw.length - 32);
  const hk = await pbkdf2(pass, hmacSalt, iterations, { name: "HMAC", hash: "SHA-256", length: 128 }, ["verify"]);
  if (!(await crypto.subtle.verify("HMAC", hk, mac as BufferSource, body as BufferSource))) throw new WrongPassphrase();
  const key = await pbkdf2(pass, salt, iterations, { name: "AES-CBC", length: 128 }, ["decrypt"]);
  return dec.decode(await crypto.subtle.decrypt({ name: "AES-CBC", iv: iv as BufferSource }, key, raw.subarray(52, raw.length - 32) as BufferSource));
}

/** Builds Evernote's format (ENEX export, and tests of the reader above). */
export async function encryptEvernote(plain: string, pass: string, iterations = EN_ITER): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const hmacSalt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(16));
  const key = await pbkdf2(pass, salt, iterations, { name: "AES-CBC", length: 128 }, ["encrypt"]);
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-CBC", iv }, key, enc.encode(plain)));
  const body = new Uint8Array(52 + ct.length);
  body.set(enc.encode("ENC0"), 0);
  body.set(salt, 4);
  body.set(hmacSalt, 20);
  body.set(iv, 36);
  body.set(ct, 52);
  const hk = await crypto.subtle.importKey(
    "raw",
    new Uint8Array(await crypto.subtle.deriveBits({ name: "PBKDF2", hash: "SHA-256", salt: hmacSalt, iterations }, await crypto.subtle.importKey("raw", enc.encode(pass), "PBKDF2", false, ["deriveBits"]), 128)),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const mac = new Uint8Array(await crypto.subtle.sign("HMAC", hk, body));
  const out = new Uint8Array(body.length + 32);
  out.set(body);
  out.set(mac, body.length);
  return b64(out);
}
